import { DeeplyReadonly, Filter } from '@cubejs-client/core';
import { useCallback, useMemo } from 'react';

import { useGetPartnerScoreYearQuarters } from '@/api/hcp/partnerscore';
import { isAllQuarters } from '@/api/hcp/partnerscore/utils/partnerScoreUtils';
import { buildYearQuarterTreeOptions } from '@/api/hcp/partnerscore/utils/yearQuarterTreeUtils';
import {
  createFiltersWithCurrentPeriod,
  createFiltersWithImmediatePreviousPeriods,
  createFiltersWithPreviousPeriods,
  getLastDate,
} from '@/components/partner/utils/partnerScoreUtils';
import { SelectItem } from '@/types/ui';

type FiltersWithPreviousPeriodsProps = {
  cubeNameArg: string;
  currentYear: number;
  currentQuarter: number;
};

export type PartnerScoreFilterUtils = {
  yearQuarterOptions: SelectItem<string>[];
  filtersWithPreviousPeriods: ({
    cubeNameArg,
    currentQuarter,
    currentYear,
  }: FiltersWithPreviousPeriodsProps) => DeeplyReadonly<Filter>[];
  filtersWithImmediatePreviousPeriods: ({
    cubeNameArg,
    currentQuarter,
    currentYear,
  }: FiltersWithPreviousPeriodsProps) => DeeplyReadonly<Filter>[];
  filtersWithCurrentPeriod: ({
    cubeNameArg,
    currentQuarter,
    currentYear,
  }: FiltersWithPreviousPeriodsProps) => DeeplyReadonly<Filter>[];
  filterQuarterDimensions: (dimensions: string[], quarter: number) => string[];
  filterSurveyList: (cubeName: string, surveyIdList: Array<string>) => Array<Filter>;
};

/**
 * Pure filter-building utilities derived from partner score date data.
 * Does NOT read or write sessionStorage — safe to call from any page
 * (e.g. CompetitorAnalysis) without triggering cross-module side effects.
 */
function usePartnerScoreFilterUtils(): PartnerScoreFilterUtils {
  const { data: date = [] } = useGetPartnerScoreYearQuarters();

  const safeDate = useMemo(() => date ?? [], [date]);
  const lastDate = useMemo(() => getLastDate(safeDate), [safeDate]);
  const yearQuarterOptions = useMemo(() => buildYearQuarterTreeOptions(safeDate), [safeDate]);

  const filtersWithPreviousPeriods = useCallback(
    ({ cubeNameArg, currentQuarter, currentYear }: FiltersWithPreviousPeriodsProps) =>
      createFiltersWithPreviousPeriods(cubeNameArg, currentYear, currentQuarter, lastDate),
    [lastDate],
  );

  const filtersWithImmediatePreviousPeriods = useCallback(
    ({ cubeNameArg, currentQuarter, currentYear }: FiltersWithPreviousPeriodsProps) =>
      createFiltersWithImmediatePreviousPeriods(cubeNameArg, currentYear, currentQuarter, safeDate),
    [safeDate],
  );

  const filtersWithCurrentPeriod = useCallback(
    ({ cubeNameArg, currentQuarter, currentYear }: FiltersWithPreviousPeriodsProps) =>
      createFiltersWithCurrentPeriod(cubeNameArg, currentYear, currentQuarter),
    [],
  );

  const filterQuarterDimensions = useCallback(
    (dimensions: string[], selectedQuarter: number): string[] =>
      isAllQuarters(selectedQuarter) ? dimensions.filter(dimension => !dimension.includes('.quarter')) : dimensions,
    [],
  );

  const filterSurveyList = useCallback((cubeName: string, currentSurveyList: Array<string>): Array<Filter> => {
    if (!currentSurveyList?.length) return [];
    return [{ member: `${cubeName}.survey_title`, operator: 'equals', values: currentSurveyList }];
  }, []);

  return {
    yearQuarterOptions,
    filtersWithPreviousPeriods,
    filtersWithImmediatePreviousPeriods,
    filtersWithCurrentPeriod,
    filterQuarterDimensions,
    filterSurveyList,
  };
}

export default usePartnerScoreFilterUtils;
